import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import HousieBackButton from '../components/HousieBackButton';
import { FONTS } from '../constants/Fonts';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { tambolaJoinRoom } from '../network/network_callbacks';
import { logger } from '../utils/logger';

const OTP_COUNT = 5;

const JoinRoomModal = ({ open, onClose, onJoined, joinRoomApi = tambolaJoinRoom }) => {
  const [otp, setOtp] = useState(Array(OTP_COUNT).fill(''));
  const [isJoining, setIsJoining] = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    setOtp(Array(OTP_COUNT).fill(''));
    setIsJoining(false);
    const timer = setTimeout(() => inputs.current[0]?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  const roomCode = otp.join('');
  const canProceed = roomCode.length === OTP_COUNT && !isJoining;

  const updateOtp = (next) => {
    setOtp(next);
  };

  const handleChange = (value, idx) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    updateOtp(next);
    if (value && idx < OTP_COUNT - 1) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_COUNT);
    if (!pasted) return;
    const next = Array(OTP_COUNT).fill('');
    pasted.split('').forEach((digit, i) => {
      next[i] = digit;
    });
    updateOtp(next);
    const focusIdx = Math.min(pasted.length, OTP_COUNT - 1);
    inputs.current[focusIdx]?.focus();
  };

  const handleResend = () => {
    updateOtp(Array(OTP_COUNT).fill(''));
    inputs.current[0]?.focus();
  };

  const handleProceed = async () => {
    if (!canProceed) return;
    try {
      setIsJoining(true);
      logger.log(`Joining room with code: ${roomCode}`);
      const response = await joinRoomApi(roomCode);
      const responseData = response?.data?.data;
      const errorCode = response?.responseCode;
      const errorData = response?.error;

      if (errorCode === 500) {
        toast.error(`${errorData?.message || 'Server Error'}`);
        setIsJoining(false);
        return;
      }

      if (
        (responseData?.join === 'fail' && responseData?.message === 'Already Joined') ||
        responseData?.join === 'success'
      ) {
        toast.success('Joined room successfully!');
        onJoined?.(responseData);
        return;
      }

      toast.error(responseData?.message || 'Unable to join room');
      setIsJoining(false);
    } catch (error) {
      console.error('Error joining room:', error);
      toast.error('Unable to join room');
      setIsJoining(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={TEXT_LABELS.RAP_HOUSIE_JOIN_ROOM_TITLE}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.55)',
        padding: 20,
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(480px, 100%)',
          minHeight: 426,
          borderRadius: 36,
          border: '2px solid #FFFFFF',
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.5) 100%)',
          boxShadow: '0px 4px 22px 0px rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 16px 28px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          color: '#003',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 392,
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            marginBottom: 38,
          }}
        >
          <HousieBackButton onClick={onClose} showLabel={false} />
          <div
            style={{
              flex: 1,
              fontFamily: FONTS.INTER_EXTRABOLD,
              fontWeight: 800,
              fontSize: 36,
              lineHeight: '44px',
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              color: '#003',
              textAlign: 'center',
              textShadow: 'none',
              marginRight: 30,
            }}
          >
            {TEXT_LABELS.RAP_HOUSIE_JOIN_ROOM_TITLE}
          </div>
        </div>

        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <div
            style={{
              fontFamily: FONTS.INTER_REGULAR,
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: 0.8,
              color: '#003',
              textAlign: 'center',
            }}
          >
            {TEXT_LABELS.RAP_HOUSIE_JOIN_ROOM_CODE_HINT}
          </div>

          <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => {
                  inputs.current[idx] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                placeholder="0"
                onChange={(e) => handleChange(e.target.value, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                onPaste={handlePaste}
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 100,
                  border: '2px solid #FFFFFF',
                  background: 'rgba(255,255,255,0.55)',
                  boxShadow: '0px 4px 22px 0px rgba(255, 255, 255, 0.7)',
                  color: '#003',
                  fontFamily: FONTS.INTER_SEMIBOLD,
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: '24px',
                  letterSpacing: 0.8,
                  textAlign: 'center',
                  outline: 'none',
                  boxSizing: 'border-box',
                  caretColor: '#822aff',
                }}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleResend}
          style={{
            marginTop: 38,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: FONTS.INTER_MEDIUM,
            fontWeight: 500,
            fontSize: 18,
            lineHeight: '22px',
            color: '#ff00f2',
            textTransform: 'uppercase',
          }}
        >
          {TEXT_LABELS.RAP_HOUSIE_JOIN_ROOM_RESEND}
        </button>

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 28,
            width: '100%',
            maxWidth: 392,
            position: 'relative',
            height: 72,
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '14.67%',
              bottom: 0,
              borderRadius: 100,
              background: 'rgba(255,255,255,0.35)',
              boxShadow: '0px 4px 22px 0px rgba(255, 255, 255, 0.7)',
            }}
          />
          <button
            type="button"
            disabled={!canProceed}
            onClick={handleProceed}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: '85.33%',
              borderRadius: 100,
              border: '2px solid #FFFFFF',
              background: canProceed ? '#E3CEFF' : 'rgba(227, 206, 255, 0.45)',
              color: canProceed ? '#822AFF' : 'rgba(130, 42, 255, 0.45)',
              fontFamily: FONTS.INTER_MEDIUM,
              fontWeight: 500,
              fontSize: 18,
              lineHeight: '22px',
              textTransform: 'uppercase',
              cursor: canProceed ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              boxShadow: '0px 4px 22px 0px rgba(255, 255, 255, 0.7)',
            }}
          >
            {isJoining ? '...' : TEXT_LABELS.RAP_HOUSIE_JOIN_ROOM_PROCEED}
          </button>
        </div>
      </div>
    </div>
  );
};

JoinRoomModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onJoined: PropTypes.func,
  joinRoomApi: PropTypes.func,
};

export default JoinRoomModal;
